"use client";

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Loader2, FileCheck } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  addProcedureSignoffs,
  loadSubstantiveProcedures,
  getSignoffEntriesFromProcedureArray,
  type ProcedureSignoffEntry,
} from "../../utils/substantive-procedures-utils";

export type ControlSignoffSection = "prep_so" | "rev_so" | "conc_so" | "eqr_so" | "partner_so";

const SIGNOFF_LABELS: Record<ControlSignoffSection, string> = {
  prep_so: "Preparer",
  rev_so: "Reviewer",
  conc_so: "Concurrent",
  eqr_so: "EQR",
  partner_so: "Partner",
};

function canSignoffSectionByRole(
  section: ControlSignoffSection,
  user: { type?: string } | null,
  engagementRole: string | null
): boolean {
  if (!user) return false;
  const userType = user.type;

  switch (section) {
    case "prep_so":
      return (
        engagementRole === "associate" ||
        engagementRole === "article" ||
        userType === "associate" ||
        userType === "article"
      );
    case "rev_so":
      return (
        engagementRole === "engagement_manager" ||
        userType === "manager" ||
        userType === "associate" ||
        engagementRole === "associate"
      );
    case "conc_so":
      return userType === "manager" || userType === "partner";
    case "eqr_so":
      return engagementRole === "eqr_partner" || engagementRole === "eqr_manager";
    case "partner_so":
      return engagementRole === "engagement_partner";
    default:
      return false;
  }
}

function isProcedurePrepCompleted(signoffs: ProcedureSignoffEntry[] | undefined): boolean {
  if (!signoffs || signoffs.length === 0) return false;
  return signoffs.some((e) => e.type === "prep_so" && e?.name && String(e.name).trim() !== "");
}

interface ProcedureSignoffDialogProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceName: string;
  procedureNames: string[];
  user: { user_name?: string; type?: string } | null;
  userEngagementRole: string | null;
  onSignoffDone?: () => void;
}

export default function ProcedureSignoffDialog({
  isOpen,
  onClose,
  workspaceName,
  procedureNames,
  user,
  userEngagementRole,
  onSignoffDone,
}: ProcedureSignoffDialogProps) {
  const [selectedProcedures, setSelectedProcedures] = useState<Set<string>>(new Set());
  const [signingOff, setSigningOff] = useState(false);
  const [error, setError] = useState<string>("");
  /** Procedure name -> sign-off entries (loaded when dialog opens) */
  const [procedureSignoffsMap, setProcedureSignoffsMap] = useState<Record<string, ProcedureSignoffEntry[]>>({});
  const [loadingSignoffs, setLoadingSignoffs] = useState(false);

  useEffect(() => {
    if (!isOpen || !workspaceName) return;
    const load = async () => {
      setLoadingSignoffs(true);
      try {
        const data = await loadSubstantiveProcedures();
        const workspaceData = data[workspaceName] || {};
        const map: Record<string, ProcedureSignoffEntry[]> = {};
        procedureNames.forEach((name) => {
          const raw = workspaceData[name];
          map[name] = getSignoffEntriesFromProcedureArray(Array.isArray(raw) ? raw : []);
        });
        setProcedureSignoffsMap(map);
      } catch {
        setProcedureSignoffsMap({});
      } finally {
        setLoadingSignoffs(false);
      }
    };
    load();
  }, [isOpen, workspaceName, procedureNames.join(",")]);

  /** All selected procedures have at least one preparer sign-off (required for Rev/CR/EQR/Partner) */
  const allSelectedHavePreparer =
    selectedProcedures.size > 0 &&
    Array.from(selectedProcedures).every((name) =>
      isProcedurePrepCompleted(procedureSignoffsMap[name])
    );

  const toggleProcedure = (name: string) => {
    setSelectedProcedures((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedProcedures.size === procedureNames.length) {
      setSelectedProcedures(new Set());
    } else {
      setSelectedProcedures(new Set(procedureNames));
    }
  };

  const handleSignoff = async (section: ControlSignoffSection) => {
    if (!user?.user_name || selectedProcedures.size === 0) return;
    setError("");
    setSigningOff(true);
    try {
      const result = await addProcedureSignoffs(
        workspaceName,
        Array.from(selectedProcedures),
        section,
        user.user_name
      );
      if (result.success) {
        onSignoffDone?.();
        onClose();
      } else {
        setError(result.error || "Failed to save sign-off");
      }
    } catch (e: any) {
      setError(e?.message || "Failed to save sign-off");
    } finally {
      setSigningOff(false);
    }
  };

  const sections: ControlSignoffSection[] = ["prep_so", "rev_so", "conc_so", "eqr_so", "partner_so"];

  const canSignoffSection = (section: ControlSignoffSection): boolean => {
    const byRole = canSignoffSectionByRole(section, user, userEngagementRole);
    if (section === "prep_so") return byRole;
    return byRole && allSelectedHavePreparer;
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md border-white/10 bg-gray-900 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <FileCheck className="h-5 w-5 text-amber-400" />
            Sign off procedures
          </DialogTitle>
          <DialogDescription className="text-white/70">
            Select one or more procedures, then choose the sign-off type. Rules and roles are the same as in other libraries.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {procedureNames.length === 0 ? (
            <p className="text-sm text-white/60">No procedures in this workspace.</p>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer">
                  <Checkbox
                    checked={procedureNames.length > 0 && selectedProcedures.size === procedureNames.length}
                    onCheckedChange={selectAll}
                    className="border-white/30 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                  />
                  Select all
                </label>
              </div>
              <div className="max-h-48 overflow-y-auto space-y-2 rounded-md border border-white/10 bg-black/20 p-2">
                {procedureNames.map((name) => (
                  <label
                    key={name}
                    className="flex items-center gap-2 text-sm text-white cursor-pointer hover:bg-white/5 rounded px-2 py-1.5"
                  >
                    <Checkbox
                      checked={selectedProcedures.has(name)}
                      onCheckedChange={() => toggleProcedure(name)}
                      className="border-white/30 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                    />
                    {name}
                  </label>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-2 pt-2">
                <span className="text-sm text-white/70">Sign off as:</span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={
                        selectedProcedures.size === 0 ||
                        signingOff ||
                        !user?.user_name ||
                        loadingSignoffs
                      }
                      className="border-white/20 text-white hover:bg-white/10"
                    >
                      {signingOff || loadingSignoffs ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        "Choose type"
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="bg-gray-900 border-white/10">
                    {sections.map((section) => {
                      const allowed = canSignoffSection(section);
                      const needsPrep = section !== "prep_so" && !allSelectedHavePreparer;
                      return (
                        <DropdownMenuItem
                          key={section}
                          disabled={!allowed}
                          onClick={() => allowed && handleSignoff(section)}
                          className="text-white focus:bg-white/10 focus:text-white"
                          title={
                            needsPrep
                              ? "Preparer sign-off required on all selected procedures first"
                              : undefined
                          }
                        >
                          {SIGNOFF_LABELS[section]}
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              {selectedProcedures.size > 0 && !allSelectedHavePreparer && (
                <p className="text-xs text-amber-400/90">
                  Reviewer, Concurrent, EQR and Partner sign-off require Preparer sign-off on all selected procedures first.
                </p>
              )}
            </>
          )}

          {error && (
            <p className="text-sm text-red-400">{error}</p>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
          <Button variant="outline" onClick={onClose} className="border-white/20 text-white hover:bg-white/10">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
